import React, { useState } from 'react';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import SymptomChecker from '../components/SymptomChecker';
import VoiceSymptomChecker from '../components/VoiceSymptomChecker';
import TelemedicineConsultation from '../components/TelemedicineConsultation';
import FamilyCareCircle from '../components/FamilyCareCircle';
import AmbulanceTracker from '../components/AmbulanceTracker';
import { whatsappService } from '../services/whatsappIntegration';
import {
  Warning as EmergencyIcon,
  LocalHospital as HospitalIcon,
  Medication as MedicationIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  AccessTime as TimeIcon,
  Favorite as HeartIcon
} from '@mui/icons-material';

interface PatientInfo {
  id: string;
  name: string;
  age: number;
  phone: string;
  emergencyContact: string;
  medicalHistory: string[];
  currentMedications: string[];
  allergies: string[];
}

interface VoiceSymptomAssessment {
  severity: 'emergency' | 'urgent' | 'moderate' | 'mild';
  confidence: number;
  recommendation: string;
  symptoms: string[];
  transcription: string;
  requiresTelemedicine: boolean;
  emergencyKeywords: string[];
}

interface DoctorDecision {
  emergencyRequired: boolean;
  urgencyLevel: 'immediate' | 'urgent' | 'routine' | 'none';
  diagnosis: string;
  instructions: string;
  prescription?: string;
  followUp: string;
  activateEmergency: boolean;
  estimatedResponseTime?: string;
  doctorNotes: string;
}

const PatientDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
  const [symptomAssessment, setSymptomAssessment] = useState<VoiceSymptomAssessment | null>(null);
  const [telemedicineOpen, setTelemedicineOpen] = useState(false);
  const [emergencyActivated, setEmergencyActivated] = useState(false);
  
  // Mock patient data - in real app this would come from API/context
  const patientInfo: PatientInfo = {
    id: '1',
    name: 'Juan Pérez',
    age: 65,
    phone: '+56912345678',
    emergencyContact: '+56987654321',
    medicalHistory: ['Hipertensión arterial', 'Diabetes tipo 2', 'Antecedentes cardíacos'],
    currentMedications: ['Aspirina 100mg', 'Enalapril 10mg', 'Metformina 850mg'],
    allergies: ['Penicilina', 'Sulfamidas']
  };
  
  const handleEmergencyClick = () => {
    setEmergencyDialogOpen(true);
  };
  
  const handleEmergencyConfirm = async () => {
    setEmergencyDialogOpen(false);
    setEmergencyActivated(true);
    
    // Send WhatsApp notifications to family
    if (symptomAssessment) {
      await whatsappService.sendEmergencyAlert(
        ['+56987654321', '+56965432100'], // Mock family phone numbers
        {
          patientName: patientInfo.name,
          emergencyId: 'EMG001',
          severity: symptomAssessment.severity as 'emergency' | 'urgent' | 'moderate',
          symptoms: symptomAssessment.symptoms,
          location: 'Providencia, Santiago',
          ambulanceId: 'AMB-001',
          estimatedArrival: '12-15 minutos'
        }
      );
    }
    
    console.log('Emergency activated!', { assessment: symptomAssessment });
  };
  
  const handleVoiceSymptomAssessment = (assessment: VoiceSymptomAssessment) => {
    setSymptomAssessment(assessment);
    
    // Auto-trigger telemedicine for urgent/emergency cases
    if (assessment.requiresTelemedicine) {
      setTelemedicineOpen(true);
    }
  };
  
  const handleTelemedicineRequest = (assessment: VoiceSymptomAssessment) => {
    setSymptomAssessment(assessment);
    setTelemedicineOpen(true);
  };
  
  const handleEmergencyActivation = async (decision: DoctorDecision) => {
    if (decision.activateEmergency) {
      setEmergencyActivated(true);
      
      // Send WhatsApp confirmation from doctor
      await whatsappService.sendDoctorEmergencyConfirmation(
        ['+56987654321', '+56965432100'], // Mock family phone numbers
        patientInfo.name,
        'Dr. María González',
        decision.diagnosis,
        decision.instructions
      );
      
      // Send ambulance updates
      await whatsappService.sendAmbulanceUpdate(
        ['+56987654321', '+56965432100'],
        patientInfo.name,
        'AMB-001',
        'dispatched',
        'Estación Central, Santiago',
        decision.estimatedResponseTime || '8-12 minutos'
      );
    }
    
    setTelemedicineOpen(false);
    console.log('Doctor decision:', decision);
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: isSmallMobile ? 2 : 4, mb: 4 }}>
      <Typography 
        variant={isSmallMobile ? "h5" : "h4"} 
        gutterBottom
        sx={{ textAlign: isSmallMobile ? 'center' : 'left' }}
      >
        Mi Panel de Salud
      </Typography>
      
      {/* Emergency Alert Button */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle1">
              Presiona el botón de emergencia si necesitas ayuda médica inmediata
            </Typography>
          </Alert>
        </Grid>

        {/* Emergency Button - Priority */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#ffebee', height: '100%' }}>
            <CardContent sx={{ 
              textAlign: 'center', 
              py: isSmallMobile ? 4 : 3,
              px: isSmallMobile ? 2 : 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%'
            }}>
              <Button
                variant="contained"
                color="error"
                size="large"
                startIcon={<EmergencyIcon fontSize={isSmallMobile ? "large" : "medium"} />}
                onClick={handleEmergencyClick}
                sx={{ 
                  fontSize: isSmallMobile ? '1.4rem' : '1.2rem', 
                  py: isSmallMobile ? 3 : 2, 
                  px: isSmallMobile ? 6 : 4,
                  borderRadius: 3,
                  boxShadow: 3,
                  minHeight: isSmallMobile ? '72px' : '56px',
                  fontWeight: 'bold',
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                  '&:hover': {
                    boxShadow: 6,
                  }
                }}
              >
                EMERGENCIA MÉDICA
              </Button>
              <Typography 
                variant={isSmallMobile ? "body1" : "body2"} 
                sx={{ 
                  mt: isSmallMobile ? 2 : 1,
                  fontSize: isSmallMobile ? '1rem' : undefined,
                  fontWeight: isSmallMobile ? 'medium' : 'normal'
                }}
              >
                Activar alerta de emergencia
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* AI Symptom Checker with Voice - Priority */}
        <Grid item xs={12} md={6}>
          <SymptomChecker 
            inline={true} 
            onAssessment={handleVoiceSymptomAssessment}
            enableVoice={true}
            onTelemedicineRequest={handleTelemedicineRequest}
          />
        </Grid>

        {/* Quick Actions - Compact */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Acciones Rápidas
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="medium"
                    startIcon={<PhoneIcon />}
                    sx={{ py: 1.5 }}
                  >
                    Llamar Doctor
                  </Button>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="medium"
                    startIcon={<LocationIcon />}
                    sx={{ py: 1.5 }}
                  >
                    Centro Cercano
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>


        {/* Family Care Circle */}
        <Grid item xs={12} md={8}>
          <FamilyCareCircle patientId="1" />
        </Grid>

        {/* Ambulance Tracker - Shows when emergency is active */}
        {emergencyActivated && (
          <Grid item xs={12}>
            <AmbulanceTracker 
              emergencyId="EMG001"
              patientName={patientInfo.name}
              isActive={emergencyActivated}
            />
          </Grid>
        )}

        {/* Medications */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <MedicationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Medicamentos Actuales
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Aspirina 100mg"
                  secondary="1 vez al día - Mañana"
                />
                <Chip label="Activo" color="success" size="small" />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Enalapril 10mg"
                  secondary="2 veces al día - Mañana y noche"
                />
                <Chip label="Activo" color="success" size="small" />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Metformina 850mg"
                  secondary="Con las comidas"
                />
                <Chip label="Activo" color="success" size="small" />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Recent Appointments */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Próximas Citas
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <TimeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Control Cardiología"
                  secondary="15 de Julio - 10:00 AM - Dr. Pérez"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <TimeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Exámenes de Sangre"
                  secondary="20 de Julio - 8:00 AM - Laboratorio"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Health Alerts */}
        <Grid item xs={12}>
          {symptomAssessment && symptomAssessment.requiresTelemedicine && !telemedicineOpen && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                📹 Consulta médica recomendada basada en sus síntomas
              </Typography>
              <Button 
                variant="contained" 
                size="small" 
                onClick={() => setTelemedicineOpen(true)}
                sx={{ mt: 1 }}
              >
                Conectar con Doctor
              </Button>
            </Alert>
          )}
          
          {emergencyActivated && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                🚨 Protocolo de emergencia activado
              </Typography>
              <Typography variant="body2">
                La ambulancia está en camino. Su familia ha sido notificada via WhatsApp.
              </Typography>
            </Alert>
          )}
          
          <Alert severity="warning">
            <Typography variant="subtitle2">
              <strong>Recordatorio:</strong> No olvide tomar su medicamento para la presión esta noche
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      {/* Telemedicine Consultation Dialog */}
      {symptomAssessment && (
        <TelemedicineConsultation
          open={telemedicineOpen}
          onClose={() => setTelemedicineOpen(false)}
          patientInfo={patientInfo}
          initialAssessment={symptomAssessment}
          onEmergencyActivation={handleEmergencyActivation}
        />
      )}

      {/* Emergency Confirmation Dialog */}
      <Dialog 
        open={emergencyDialogOpen} 
        onClose={() => setEmergencyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', color: 'error.main' }}>
          <EmergencyIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h5" fontWeight="bold">
            Confirmar Emergencia Médica
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              ⚠️ Está a punto de activar una alerta de emergencia médica
            </Typography>
          </Alert>
          
          {symptomAssessment && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Evaluación de síntomas:</strong>
              </Typography>
              <Chip 
                label={symptomAssessment.severity === 'emergency' ? 'EMERGENCIA' : 
                       symptomAssessment.severity === 'urgent' ? 'URGENTE' : 
                       symptomAssessment.severity.toUpperCase()}
                color={symptomAssessment.severity === 'emergency' ? 'error' : 
                       symptomAssessment.severity === 'urgent' ? 'warning' : 'info'}
                size="small"
              />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {symptomAssessment.recommendation}
              </Typography>
            </Box>
          )}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Al confirmar, se activará inmediatamente:
          </Typography>
          
          <Box component="ul" sx={{ pl: 2 }}>
            <li>🚨 Alerta a servicios de emergencia</li>
            <li>👨‍⚕️ Notificación a su médico</li>
            <li>👨‍👩‍👧‍👦 Alerta a su círculo de cuidado</li>
            <li>🚑 Despacho de ambulancia si es necesario</li>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Solo active la emergencia si realmente necesita ayuda médica inmediata.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          <Button 
            onClick={() => setEmergencyDialogOpen(false)}
            variant="outlined"
            size="large"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleEmergencyConfirm}
            variant="contained"
            color="error"
            size="large"
            startIcon={<EmergencyIcon />}
            sx={{ fontWeight: 'bold' }}
          >
            CONFIRMAR EMERGENCIA
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PatientDashboard;